// ============================================================================
// CONSULTING REPORT HTML RENDERER  v3
// Rich visual design: section icons, color-coded severity, metric highlights,
// numbered cards, phase timeline, outcome direction chips.
// ============================================================================

import { getReportTheme, DEFAULT_REPORT_THEME } from '@process-advisor/process-miner/consultant/reportThemes';

// ── Palette ───────────────────────────────────────────────────────────────────
// Theme-driven: colour definitions live in reportThemes.js ('servicenow' and
// 'atlassian'). P is mutated in place by applyReportTheme so all render
// helpers keep referencing the same object.
const P = { ...getReportTheme(DEFAULT_REPORT_THEME).html };

export function applyReportTheme(themeId) {
  Object.assign(P, getReportTheme(themeId).html);
  rebuildDesignTokens();
}

const esc = (v) =>
  String(v ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const fmt = (text = '') =>
  esc(text).replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br/>');


const cleanSentenceEnd = (value = '') => {
  let s = String(value ?? '').replace(/\s+/g, ' ').trim();
  if (!s) return '';
  s = s.replace(/\s+[A-Z]$/, '').trim();
  const lastStop = Math.max(s.lastIndexOf('.'), s.lastIndexOf('!'), s.lastIndexOf('?'));
  if (lastStop >= 0 && lastStop >= Math.floor(s.length * 0.55)) s = s.slice(0, lastStop + 1).trim();
  if (s && !/[.!?…]$/.test(s)) s += '.';
  return s;
};

const truncateAtSentence = (value = '', max = 900) => {
  const s = String(value ?? '').replace(/\s+/g, ' ').trim();
  if (!s || s.length <= max) return cleanSentenceEnd(s);
  const slice = s.slice(0, max).trim();
  const lastStop = Math.max(slice.lastIndexOf('.'), slice.lastIndexOf('!'), slice.lastIndexOf('?'));
  if (lastStop >= Math.floor(max * 0.55)) return cleanSentenceEnd(slice.slice(0, lastStop + 1));
  return slice.replace(/\s+\S*$/, '').trim() + '…';
};

const toReportTitleCase = (value = '') => {
  const minor = new Set(['and','or','the','a','an','to','of','in','on','for','with','vs','via']);
  return String(value ?? '')
    .replace(/[_-]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((word, i) => {
      if (word.includes('→') || /^[A-Z0-9%/]+$/.test(word)) return word;
      const lower = word.toLowerCase();
      if (i > 0 && minor.has(lower)) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
};

const formatAttributeTopValuesHtml = (value) => {
  const raw = Array.isArray(value) ? value.map(formatCellValue).join('; ') : formatCellValue(value);
  const items = raw.split(/\s*;\s*/).map(x => x.trim()).filter(Boolean).slice(0, 8);
  if (!items.length) return esc(raw);
  return `<div class="cr-attr-chip-list">${items.map(item => {
    const parts = item.split(/\s*[·:]\s*/).filter(Boolean);
    const label = parts[0] || item;
    const count = parts.slice(1).join(' · ');
    return `<span class="cr-attr-chip"><span>${esc(label)}</span>${count ? `<strong>${esc(count)}</strong>` : ''}</span>`;
  }).join('')}</div>`;
};

// ── Design tokens ─────────────────────────────────────────────────────────────
// These capture palette VALUES, so they must be rebuilt when the theme changes.
let PRIORITY, REC_TYPE, ASSESSMENT, CONFIDENCE, SOURCE, OUTCOME_DIR, PHASE_COLORS, THEME_ACCENT_COLORS;

function rebuildDesignTokens() {
PRIORITY = {
  'Critical': { bg:P.dangerBg, color:P.dangerDark, dot:P.danger },
  'High':     { bg:P.warnBg, color:P.warnDark, dot:P.warn },
  'Medium':   { bg:P.tintDeep, color:P.primaryDark, dot:P.primary },
};
REC_TYPE = {
  'Workflow':          { bg:P.tint, color:P.primary },
  'Automation':        { bg:P.tintDeep, color:P.primaryDark },
  'Governance':        { bg:P.tint, color:P.primary },
  'Measurement':       { bg:P.accentTint, color:P.accentDark },
  'Operating Practice':{ bg:P.warnBg, color:P.warnDark },
};
ASSESSMENT = {
  'Critical Intervention Required': { bg:P.dangerBg, color:P.dangerDark, bar:P.danger },
  'Major Improvement Needed':       { bg:P.warnBg, color:P.warnDark, bar:P.warn },
  'Significant Opportunity':        { bg:P.tintDeep, color:P.primaryDark, bar:P.primary },
  'Moderate Opportunity':           { bg:P.tint, color:P.primary, bar:P.primaryAlt },
  'Targeted Optimisation':          { bg:P.pageBg, color:P.text, bar:P.muted },
};
CONFIDENCE = {
  'High':     { bg:P.tint, color:P.primary },
  'Moderate': { bg:P.tintDeep, color:P.primaryDark },
  'Low':      { bg:P.warnBg, color:P.warnDark },
};
SOURCE = {
  'process_summary':   { label:'Process',     color:P.primary },
  'transitions':       { label:'Transitions', color:P.warn },
  'transition_signals':{ label:'Transitions', color:P.warn },
  'variants':          { label:'Variants',    color:P.primaryAlt },
  'variant_signals':   { label:'Variants',    color:P.primaryAlt },
  'workload_signals':  { label:'Attributes',    color:P.warn },
  'status_model':      { label:'Status',      color:P.muted },
  'time_intelligence': { label:'Time Intel',  color:P.accent },
  'time_intel':        { label:'Time Intel',  color:P.accent },
  'sla_signals':       { label:'SLA',         color:P.purple },
};
OUTCOME_DIR = {
  'Reduce':   { bg:P.dangerBg, color:P.dangerDark, icon:'↓' },
  'Increase': { bg:P.tint, color:P.primary, icon:'↑' },
  'Improve':  { bg:P.tintDeep, color:P.primaryDark, icon:'↗' },
  'Eliminate':{ bg:P.tint, color:P.primary, icon:'✕' },
  'Maintain': { bg:P.pageBg, color:P.text, icon:'→' },
};
PHASE_COLORS = [P.primary,P.warn,P.danger,P.warn,P.primaryAlt];
THEME_ACCENT_COLORS = [P.primary,P.warn,P.danger,P.warn,P.primaryAlt,P.accent];
}
rebuildDesignTokens();

// Getters read the live token maps at call time, so they stay theme-correct.
const getPri = (p) => PRIORITY[p] || PRIORITY['Medium'];
const getRT = (t) => REC_TYPE[t] || { bg:P.pageBg, color:P.text };
const getAss = (a) => ASSESSMENT[a] || ASSESSMENT['Significant Opportunity'];
const getConf = (c) => CONFIDENCE[c] || CONFIDENCE['Moderate'];
const srcBadge = (src) => {
  const k = (src||'').toLowerCase().replace(/\s+/g,'_');
  const m = SOURCE[k] || { label: src || 'Data', color:P.muted };
  return `<span style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;padding:2px 8px;border-radius:999px;background:${m.color}18;color:${m.color};border:1px solid ${m.color}35;white-space:nowrap;">${esc(m.label)}</span>`;
};
const getDir = (d) => OUTCOME_DIR[d] || { bg:P.tint, color:P.primary, icon:'↗' };

// ── SVG Icons ──────────────────────────────────────────────────────────────────
const SVG = {
  scope:      `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>`,
  signals:    `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>`,
  diagnostic: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>`,
  recs:       `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>`,
  future:     `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>`,
  automation: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>`,
  roadmap:    `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="3" y1="12" x2="21" y2="12"/><path d="M3 6l9-3 9 3M3 18l9 3 9-3"/></svg>`,
  outcomes:   `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>`,
  guardrails: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>`,
  note:       `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>`,
  bolt:       `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>`,
  check:      `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>`,
  shield:     `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>`,
};

const sectionHeader = (svgKey, title, accent) => `
  <div style="display:flex;align-items:center;gap:10px;margin-bottom:14px;">
    <div style="width:34px;height:34px;border-radius:8px;background:${accent};display:flex;align-items:center;justify-content:center;color:#fff;flex-shrink:0;">
      ${SVG[svgKey]}
    </div>
    <h2 class="cr-section-title" data-toc="${esc(title)}">${esc(title)}</h2>
  </div>`;

const badge = (text, bg, color, dot) =>
  `<span style="display:inline-flex;align-items:center;gap:5px;padding:3px 9px;border-radius:5px;font-size:11px;font-weight:700;background:${bg};color:${color};">${dot ? `<span style="display:inline-block;width:7px;height:7px;border-radius:50%;background:${dot};flex-shrink:0;"></span>` : ''}${esc(text)}</span>`;

const signalSeverity = (label, value) => {
  const t = (label + ' ' + value).toLowerCase();
  if (/rework|delay|fragmentation|poor|critical|low efficiency|low flow|unpredictab|excessive|regressive|bottleneck/i.test(t)) return 'danger';
  if (/high.*variant|moderate|risk|warn/i.test(t)) return 'warning';
  if (/good|efficient|stable|low rework|strong|populated|reliable|discipline|foundation/i.test(t)) return 'ok';
  return '';
};

const parsePhase = (str) => {
  const m = str.match(/^(Phase \d+[^:]*):?\s*(.+)/i);
  if (m) return { label: m[1].trim(), body: m[2].trim() };
  const m2 = str.match(/^([^:]+):\s*(.+)/);
  if (m2) return { label: m2[1].trim(), body: m2[2].trim() };
  return { label: '', body: str };
};

// ── CSS ───────────────────────────────────────────────────────────────────────
// Built per-render so the active theme's palette is always applied.
const buildCSS = () => `
  html, body { margin: 0; padding: 0; box-sizing: border-box; }
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif; background: ${P.pageBg}; color: ${P.ink}; padding: 0; -webkit-font-smoothing: antialiased; text-rendering: optimizeLegibility; }
  .cr-signal-value, .cr-health-score, .cr-health-metric-value, .cr-num-pill, .cr-evidence-value, .cr-rec-num-val, .cr-bar-value { font-variant-numeric: tabular-nums; }
  .cr-wrap { display: block; padding: 16px 16px 10px; max-width: 920px; margin: 0 auto; }
  .cr-doc-header { background: #fff; border: 1px solid ${P.borderAlt}; border-radius: 10px; padding: 12px 18px; margin-bottom: 8px; display: flex; justify-content: space-between; align-items: center; }
  .cr-doc-header-left { display: flex; flex-direction: column; gap: 4px; }
  .cr-doc-header-title { font-size: 16px; font-weight: 700; color: ${P.ink}; }
  .cr-doc-header-type { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .06em; color: ${P.primary}; }
  .cr-doc-header-time { font-size: 12px; color: ${P.muted}; }
  .cr-section { background: #fff; border: 1px solid ${P.borderAlt}; border-radius: 10px; padding: 16px 18px; margin-bottom: 8px; }
  .cr-hero { background: #fff; border: 1px solid ${P.borderAlt}; border-radius: 10px; padding: 18px 20px; position: relative; overflow: hidden; margin-bottom: 8px; }
  .cr-hero::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 4px; background: linear-gradient(90deg, ${P.primaryDark} 0%, ${P.primary} 55%, ${P.accent} 100%); }
  .cr-hero-eyebrow { font-size: 10px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; color: ${P.primary}; margin-bottom: 10px; display: flex; align-items: center; gap: 6px; }
  .cr-hero-eyebrow::before { content: ''; display: inline-block; width: 16px; height: 2px; background: ${P.primary}; border-radius: 2px; }
  .cr-hero-headline { font-size: 22px; font-weight:700; line-height: 1.25; color: ${P.ink}; margin-bottom: 12px; }
  .cr-section-title { font-size: 17px; font-weight: 700; color: ${P.ink}; display: flex; align-items: baseline; gap: 8px; }
  .cr-sec-num { font-size: 12px; font-weight:700; color: ${P.primary}; letter-spacing: .02em; }
  .cr-toc { background: #fff; border: 1px solid ${P.borderAlt}; border-radius: 10px; padding: 14px 18px; margin-bottom: 8px; }
  .cr-toc-title { font-size: 11px; font-weight:700; text-transform: uppercase; letter-spacing: .06em; color: ${P.muted}; margin-bottom: 10px; }
  .cr-toc-list { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 4px 24px; margin: 0; padding: 0; list-style: none; counter-reset: toc; }
  .cr-toc-list li { font-size: 12.5px; line-height: 1.75; counter-increment: toc; }
  .cr-toc-list li::before { content: counter(toc) '.'; color: ${P.primary}; font-weight:700; margin-right: 7px; }
  .cr-toc-list a { color: ${P.text}; text-decoration: none; font-weight: 600; }
  .cr-toc-list a:hover { color: ${P.primary}; text-decoration: underline; }
  .cr-hero-summary { font-size: 13.5px; line-height: 1.6; color: ${P.text}; padding-bottom: 13px; border-bottom: 1px solid ${P.border}; margin-bottom: 12px; }
  .cr-hero-meta { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  .cr-hero-position { display: inline-flex; align-items: center; gap: 6px; padding: 5px 12px; border-radius: 6px; font-size: 12px; font-weight: 700; }
  .cr-hero-rationale { font-size: 13px; line-height: 1.6; color: ${P.text}; margin-top: 10px; }
  .cr-signal-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px,1fr)); gap: 8px; }
  .cr-signal-card { background: ${P.card}; border: 1px solid ${P.border}; border-radius: 10px; padding: 12px 14px; display: flex; flex-direction: column; gap: 4px; transition: box-shadow .15s; }
  .cr-signal-card:hover { box-shadow: 0 3px 12px rgba(9,30,66,.1); }
  .cr-signal-top { display: flex; justify-content: space-between; align-items: flex-start; gap: 6px; margin-bottom: 2px; flex-wrap: wrap; }
  .cr-signal-label { min-width: 0; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: ${P.muted}; }
  .cr-signal-value { font-size: 21px; font-weight:800; color: ${P.ink}; line-height: 1.15; }
  .cr-signal-why { font-size: 12px; line-height: 1.5; color: ${P.text}; }
  .cr-signal-card.danger  { border-color: ${P.dangerBorder}; background: ${P.dangerTint}; }
  .cr-signal-card.danger .cr-signal-value  { color: ${P.dangerDark}; }
  .cr-signal-card.warning { border-color: ${P.warnBorder}; background: ${P.warnTint}; }
  .cr-signal-card.warning .cr-signal-value { color: ${P.warnDark}; }
  .cr-signal-card.ok      { border-color: ${P.tintBorderStrong}; background: ${P.tintBg}; }
  .cr-signal-card.ok .cr-signal-value      { color: ${P.primary}; }
  .cr-theme-card { border: 1px solid ${P.border}; border-radius: 10px; padding: 14px; margin-bottom: 8px; background: ${P.card}; position: relative; overflow: hidden; }
  .cr-theme-card:last-child { margin-bottom: 0; }
  .cr-theme-card::before { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 4px; background: var(--accent, ${P.primary}); }
  .cr-theme-header { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; padding-left: 10px; }
  .cr-theme-num { width: 24px; height: 24px; border-radius: 50%; color: #fff; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; flex-shrink: 0; }
  .cr-theme-name { font-size: 15px; font-weight: 700; color: ${P.ink}; }
  .cr-theme-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px; padding-left: 10px; }
  .cr-theme-panel { background: #fff; border: 1px solid ${P.borderAlt}; border-radius: 8px; padding: 10px 12px; }
  .cr-theme-plabel { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; margin-bottom: 5px; }
  .cr-theme-plabel.evidence { color: ${P.warn}; }
  .cr-theme-plabel.interpretation { color: ${P.primary}; }
  .cr-theme-plabel.business { color: ${P.danger}; }
  .cr-theme-ptext { font-size: 12px; line-height: 1.6; color: ${P.text}; }
  .cr-rec-card { border: 1px solid ${P.border}; border-radius: 10px; background: #fff; margin-bottom: 8px; overflow: hidden; }
  .cr-rec-card:last-child { margin-bottom: 0; }
  .cr-rec-header-bar { display: flex; align-items: stretch; border-bottom: 1px solid ${P.border}; }
  .cr-rec-num-block { padding: 14px 16px; display: flex; flex-direction: column; align-items: center; justify-content: center; min-width: 52px; border-right: 1px solid ${P.border}; background: ${P.card}; }
  .cr-rec-num-label { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: ${P.muted}; }
  .cr-rec-num-val { font-size: 21px; font-weight:800; color: ${P.ink}; line-height: 1; margin-top: 2px; }
  .cr-rec-title-block { flex: 1; padding: 12px 16px; }
  .cr-rec-title { font-size: 16px; font-weight: 700; color: ${P.ink}; margin-bottom: 6px; }
  .cr-rec-badges { display: flex; flex-wrap: wrap; gap: 5px; }
  .cr-rec-body { display: grid; grid-template-columns: 1fr 1fr; }
  .cr-rec-body-bottom { display: grid; grid-template-columns: 1fr 1fr; border-top: 1px solid ${P.border}; }
  .cr-rec-cell { padding: 10px 14px; border-right: 1px solid ${P.border}; }
  .cr-rec-cell:last-child { border-right: none; }
  .cr-rec-cell-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: ${P.muted}; margin-bottom: 5px; display: flex; align-items: center; gap: 4px; }
  .cr-rec-cell-label.benefit { color: ${P.primary}; }
  .cr-rec-cell-label.owner   { color: ${P.warn}; }
  .cr-rec-cell-text { font-size: 13px; line-height: 1.6; color: ${P.text}; }
  .cr-principles-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px,1fr)); gap: 8px; }
  .cr-principle-card { display: flex; align-items: flex-start; gap: 10px; background: linear-gradient(135deg, ${P.tintBg}, ${P.tint}); border: 1px solid ${P.tintBorder}; border-radius: 8px; padding: 10px 12px; font-size: 13px; font-weight: 600; color: ${P.primaryDark}; line-height: 1.4; }
  .cr-auto-grid { display: flex; flex-direction: column; gap: 6px; margin-top: 8px; }
  .cr-auto-item { display: flex; align-items: center; gap: 10px; background: ${P.pageBg}; border-radius: 7px; padding: 10px 14px; border-left: 3px solid ${P.primary}; font-size: 13px; color: ${P.ink}; line-height: 1.4; }
  .cr-auto-icon { width: 26px; height: 26px; border-radius: 6px; background: ${P.primary}; color: #fff; display: flex; align-items: center; justify-content: center; font-size: 13px; flex-shrink: 0; }
  .cr-phases { display: flex; flex-direction: column; gap: 8px; margin-top: 8px; }
  .cr-phase { display: flex; align-items: flex-start; gap: 12px; background: ${P.card}; border-radius: 8px; padding: 12px 14px; border: 1px solid ${P.border}; }
  .cr-phase-circle { width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight:700; color: #fff; flex-shrink: 0; }
  .cr-phase-text { font-size: 13px; color: ${P.ink}; line-height: 1.55; flex: 1; }
  .cr-phase-text strong { font-weight: 700; display: block; margin-bottom: 2px; }
  .cr-outcome-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px,1fr)); gap: 8px; }
  .cr-outcome-card { background: ${P.card}; border: 1px solid ${P.border}; border-radius: 10px; padding: 14px; display: flex; flex-direction: column; gap: 6px; }
  .cr-outcome-dir-chip { display: inline-flex; align-items: center; gap: 5px; padding: 3px 10px; border-radius: 999px; font-size: 11px; font-weight: 700; width: fit-content; }
  .cr-outcome-text { font-size: 13px; font-weight: 600; color: ${P.ink}; line-height: 1.4; }
  .cr-outcome-why  { font-size: 12px; line-height: 1.5; color: ${P.muted}; }
  .cr-guardrail-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px,1fr)); gap: 8px; }
  .cr-guardrail-card { background: ${P.warnTintAlt}; border: 1px solid ${P.warnBorder}; border-radius: 10px; padding: 14px; display: flex; flex-direction: column; gap: 5px; }
  .cr-guardrail-header { display: flex; align-items: center; gap: 8px; }
  .cr-guardrail-icon { width: 24px; height: 24px; border-radius: 6px; background: ${P.warn}; color: #fff; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .cr-guardrail-title { font-size: 13px; font-weight: 700; color: ${P.ink}; }
  .cr-guardrail-desc { font-size: 12px; line-height: 1.6; color: ${P.text}; }
  .cr-note { background: linear-gradient(135deg, ${P.tintBg} 0%, ${P.tint} 100%); border: 1px solid ${P.tintBorder}; border-radius: 10px; padding: 16px 18px; display: flex; gap: 12px; align-items: flex-start; }
  .cr-note-icon { width: 32px; height: 32px; border-radius: 8px; background: ${P.primary}; display: flex; align-items: center; justify-content: center; color: #fff; flex-shrink: 0; }
  .cr-note-text { font-size: 13px; line-height: 1.7; color: ${P.primaryDark}; }

  .cr-evidence-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 10px; }
  .cr-evidence-card { background:${P.card}; border:1px solid ${P.border}; border-radius:10px; padding:12px 14px; }
  .cr-evidence-label { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing: .06em; color:${P.muted}; margin-bottom:5px; }
  .cr-evidence-value { font-size:17px; font-weight:800; color:${P.ink}; line-height:1.2; }
  .cr-subsection-title { font-size:14px; font-weight:700; color:${P.ink}; margin:16px 0 8px; display:flex; align-items:center; gap:6px; }
  .cr-table-wrap { overflow-x:auto; border:1px solid ${P.border}; border-radius:9px; background:#fff; margin-top:8px; }
  .cr-data-table { width:100%; border-collapse:collapse; font-size:12px; }
  .cr-data-table th { text-align:left; background:${P.card}; color:${P.text}; font-size:10px; text-transform:uppercase; letter-spacing:.06em; padding:8px 10px; border-bottom:1px solid ${P.border}; white-space:nowrap; }
  .cr-data-table td { padding:8px 10px; border-bottom:1px solid ${P.borderFaint}; color:${P.ink}; vertical-align:top; line-height:1.35; }
  .cr-data-table tr:last-child td { border-bottom:none; }
  .cr-bar-list { display:flex; flex-direction:column; gap:7px; }
  .cr-bar-row { display:grid; grid-template-columns:minmax(140px, 1fr) 2fr 72px; gap:8px; align-items:center; font-size:12px; }
  .cr-bar-label { color:${P.ink}; font-weight:700; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
  .cr-bar-track { height:10px; border-radius:999px; background:${P.border}; overflow:hidden; }
  .cr-bar-fill { height:100%; border-radius:999px; background:${P.primary}; }
  .cr-bar-value { color:${P.text}; font-weight:700; text-align:right; }

  .cr-flow-visual { background:${P.cardAlt2}; border:1px solid ${P.borderStrong}; border-radius:12px; padding:14px; margin:10px 0 14px; }
  .cr-flow-main { display:grid; grid-template-columns:1fr auto 1fr auto 1fr auto 1fr; gap:8px; align-items:center; }
  .cr-flow-node { border:1px solid ${P.tintBorder}; background:${P.tintBg}; color:${P.primaryDark}; border-radius:10px; padding:10px 12px; text-align:center; font-size:13px; font-weight:700; min-height:44px; display:flex; align-items:center; justify-content:center; }
  .cr-flow-node.end { border-color:${P.successBorder}; background:${P.successBg}; color:${P.success}; }
  .cr-flow-arrow { color:${P.primary}; font-weight:700; text-align:center; font-size:16px; }
  .cr-flow-edge-note { grid-column:1 / -1; display:grid; grid-template-columns:repeat(auto-fit,minmax(190px,1fr)); gap:8px; margin-top:10px; }
  .cr-flow-note { background:#fff; border:1px solid ${P.border}; border-radius:9px; padding:9px 11px; font-size:12px; line-height:1.45; color:${P.text}; }
  .cr-flow-note strong { color:${P.ink}; }
  .cr-flow-alert { border:1px solid ${P.warnAlt}; background:${P.warnTint}; border-radius:10px; padding:12px 14px; margin:12px 0; display:grid; grid-template-columns:auto 1fr; gap:10px; align-items:start; }
  .cr-flow-alert-icon { width:28px; height:28px; border-radius:8px; background:${P.warn}; color:#fff; display:flex; align-items:center; justify-content:center; font-weight:700; }
  .cr-flow-alert-title { font-size:14px; font-weight:700; color:${P.ink}; margin-bottom:4px; }
  .cr-flow-alert-copy { font-size:12px; line-height:1.55; color:${P.text}; }
  .cr-loop-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(260px,1fr)); gap:10px; margin:10px 0 14px; }
  .cr-loop-card { border:1px solid ${P.dangerBorderSoft}; background:${P.dangerTint}; border-radius:10px; padding:12px 14px; }
  .cr-loop-title { font-size:13px; font-weight:700; color:${P.dangerDark}; margin-bottom:5px; }
  .cr-loop-copy { font-size:12px; line-height:1.5; color:${P.text}; }
  .cr-transition-compact { display:grid; grid-template-columns:repeat(auto-fit,minmax(260px,1fr)); gap:8px; }
  .cr-transition-card { border:1px solid ${P.border}; background:#fff; border-radius:9px; padding:10px 12px; font-size:12px; }
  .cr-transition-path { font-weight:700; color:${P.ink}; margin-bottom:4px; }
  .cr-transition-meta { color:${P.text}; line-height:1.4; }

  .cr-map-wrap { display:grid; grid-template-columns:0.8fr 1.2fr; gap:12px; align-items:start; }
  .cr-node-cloud { display:flex; flex-wrap:wrap; gap:7px; }
  .cr-node-pill { border:1px solid ${P.tintBorder}; background:${P.tintBg}; color:${P.primaryDark}; padding:6px 9px; border-radius:999px; font-size:12px; font-weight:700; }
  .cr-edge-list { display:flex; flex-direction:column; gap:6px; }
  .cr-edge-row { display:grid; grid-template-columns:1fr auto 1fr auto; gap:8px; align-items:center; padding:8px 10px; border:1px solid ${P.border}; border-radius:8px; background:${P.card}; font-size:12px; }
  .cr-edge-activity { font-weight:700; color:${P.ink}; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
  .cr-edge-arrow { color:${P.primary}; font-weight:700; }
  .cr-edge-meta { color:${P.text}; font-size:11px; font-weight:700; white-space:nowrap; }
  .cr-curated-grid { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
  .cr-curated-card { border:1px solid ${P.border}; border-radius:10px; background:${P.card}; padding:12px 14px; }
  .cr-curated-title { font-size:13px; font-weight:700; color:${P.ink}; margin-bottom:5px; }
  .cr-curated-meta { display:flex; flex-wrap:wrap; gap:5px; margin-bottom:7px; }
  .cr-small-chip { font-size:10px; font-weight:700; color:${P.text}; background:${P.border}; border-radius:999px; padding:3px 7px; }
  .cr-curated-text { font-size:12px; line-height:1.55; color:${P.text}; }
  .cr-advisor-card { border:1px solid ${P.borderStrong}; border-radius:10px; background:#fff; padding:12px 14px; margin-bottom:8px; }
  .cr-advisor-title { font-size:13px; font-weight:700; color:${P.primaryDark}; margin-bottom:6px; }
  .cr-advisor-text { font-size:12px; line-height:1.6; color:${P.text}; max-height:170px; overflow:hidden; }

  .cr-interpretation { margin-top:8px; font-size:12px; line-height:1.55; color:${P.text}; background:${P.cardAlt}; border:1px solid ${P.border}; border-radius:8px; padding:9px 11px; }
  .cr-evidence-intro { font-size:13px; line-height:1.6; color:${P.text}; margin-bottom:12px; }
  .cr-evidence-table-title { display:flex; align-items:center; justify-content:space-between; gap:10px; margin:16px 0 6px; }
  .cr-evidence-table-title span:first-child { font-size:14px; font-weight:700; color:${P.ink}; }
  .cr-evidence-table-title span:last-child { font-size:10px; font-weight:700; color:${P.muted}; text-transform:uppercase; letter-spacing:.06em; }
  .cr-advisor-synthesis-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(260px,1fr)); gap:10px; }
  .cr-advisor-synthesis-card { border:1px solid ${P.tintBorder}; background:${P.tintBg2}; border-radius:10px; padding:13px 14px; }
  .cr-advisor-source { font-size:10px; font-weight:700; color:${P.primary}; text-transform:uppercase; letter-spacing: .06em; margin-bottom:6px; }
  .cr-advisor-point { font-size:14px; font-weight:700; color:${P.ink}; line-height:1.35; margin-bottom:7px; }
  .cr-advisor-basis { font-size:12px; color:${P.text}; line-height:1.55; margin-bottom:7px; }
  .cr-advisor-relevance { font-size:12px; color:${P.primaryDark}; line-height:1.55; background:${P.tint}; border-radius:7px; padding:8px 9px; }
  .cr-field-values { color:${P.text}; font-size:11px; line-height:1.45; }
  .cr-attr-chip-list { display:flex; flex-wrap:wrap; gap:5px; }
  .cr-attr-chip { display:inline-flex; align-items:center; gap:5px; border:1px solid ${P.borderStrong}; background:${P.cardAlt}; border-radius:999px; padding:3px 8px; font-size:11px; color:${P.ink}; line-height:1.35; }
  .cr-attr-chip strong { color:${P.primary}; font-weight:700; }
  .cr-raw-source { margin-top:12px; }
  .cr-raw-source summary { cursor:pointer; color:${P.primary}; font-size:12px; font-weight:700; }
  .cr-raw-source .cr-advisor-card { margin-top:8px; }

  .cr-section-kicker { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing: .06em; color:${P.primary}; margin-bottom:8px; }
  .cr-critical-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(240px,1fr)); gap:10px; margin-bottom:12px; }
  .cr-critical-card { border:1px solid ${P.borderStrong}; border-radius:10px; background:#fff; padding:13px 14px; }
  .cr-critical-title { font-size:12px; font-weight:700; color:${P.ink}; line-height:1.35; margin-bottom:5px; }
  .cr-critical-text { font-size:12px; color:${P.text}; line-height:1.5; }
  .cr-evidence-rank-table { width:100%; border-collapse:separate; border-spacing:0 7px; font-size:12px; }
  .cr-evidence-rank-table th { text-align:left; color:${P.muted}; font-size:10px; text-transform:uppercase; letter-spacing:.06em; padding:0 8px 2px; }
  .cr-evidence-rank-table td { background:${P.card}; border-top:1px solid ${P.border}; border-bottom:1px solid ${P.border}; padding:9px 8px; vertical-align:middle; color:${P.ink}; }
  .cr-evidence-rank-table td:first-child { border-left:1px solid ${P.border}; border-radius:8px 0 0 8px; width:42px; text-align:center; font-weight:700; color:${P.primary}; }
  .cr-evidence-rank-table td:last-child { border-right:1px solid ${P.border}; border-radius:0 8px 8px 0; }
  .cr-evidence-main { font-weight:700; color:${P.ink}; line-height:1.35; }
  .cr-evidence-sub { font-size:11px; color:${P.mutedAlt}; line-height:1.4; margin-top:3px; }
  .cr-num-pill { display:inline-flex; align-items:center; justify-content:center; min-width:54px; padding:4px 8px; border-radius:999px; background:${P.tint}; color:${P.primaryDark}; font-weight:700; }
  .cr-signal-pill { display:inline-flex; align-items:center; padding:4px 8px; border-radius:999px; font-size:10px; font-weight:700; background:${P.pageBg}; color:${P.text}; white-space:nowrap; }
  .cr-action-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(320px,1fr)); gap:10px; }
  .cr-action-card { border:1px solid ${P.borderStrong}; border-radius:11px; background:#fff; overflow:hidden; }
  .cr-action-head { display:flex; align-items:flex-start; gap:10px; padding:12px 14px; background:${P.card}; border-bottom:1px solid ${P.border}; }
  .cr-action-num { width:28px; height:28px; border-radius:8px; background:${P.primary}; color:#fff; display:flex; align-items:center; justify-content:center; font-size:12px; font-weight:700; flex-shrink:0; }
  .cr-action-title { font-size:14px; font-weight:700; color:${P.ink}; line-height:1.35; }
  .cr-action-body { padding:12px 14px; display:grid; gap:9px; }
  .cr-action-label { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing: .06em; color:${P.muted}; margin-bottom:3px; }
  .cr-action-copy { font-size:12px; color:${P.text}; line-height:1.55; }
  .cr-linked-note { border-left:3px solid ${P.primary}; background:${P.tintBg}; padding:9px 11px; border-radius:7px; font-size:12px; color:${P.primaryDark}; line-height:1.5; }

  .cr-health-panel { display:grid; grid-template-columns:220px 1fr; gap:10px; align-items:stretch; }
  .cr-health-score-card { border:1px solid ${P.tintBorder}; background:linear-gradient(135deg,${P.tintBg2},${P.tint}); border-radius:12px; padding:16px; display:flex; flex-direction:column; justify-content:center; align-items:center; text-align:center; }
  .cr-health-score { font-size:42px; font-weight:800; line-height:1; color:${P.primaryDark}; letter-spacing:-.04em; }
  .cr-health-score-label { font-size:11px; font-weight:700; color:${P.primary}; text-transform:uppercase; letter-spacing: .06em; margin-top:6px; }
  .cr-health-verdict { font-size:13px; font-weight:700; color:${P.ink}; line-height:1.35; margin-top:10px; }
  .cr-health-metrics { display:grid; grid-template-columns:repeat(auto-fit,minmax(125px,1fr)); gap:8px; }
  .cr-health-metric { border:1px solid ${P.border}; background:#fff; border-radius:9px; padding:10px 11px; }
  .cr-health-metric-label { font-size:9px; font-weight:700; color:${P.muted}; text-transform:uppercase; letter-spacing: .06em; margin-bottom:4px; }
  .cr-health-metric-value { font-size:16px; font-weight:700; color:${P.ink}; line-height:1.2; }
  .cr-health-excerpt { margin-top:10px; border-left:3px solid ${P.primary}; background:${P.tintBg2}; border-radius:8px; padding:10px 12px; font-size:12px; color:${P.text}; line-height:1.55; }
  .cr-doc-footer { border-top: 2px solid ${P.borderStrong}; margin-top: 6px; padding: 14px 20px 18px; display: flex; flex-direction: column; gap: 4px; }
  .cr-doc-footer-brand { font-size: 11px; font-weight:700; text-transform: uppercase; letter-spacing: .06em; color: ${P.primary}; }
  .cr-doc-footer-text { font-size: 11px; line-height: 1.55; color: ${P.muted}; }
  @media (max-width: 760px) { .cr-health-panel { grid-template-columns:1fr; } }
  @page { margin: 14mm 12mm; }
  @media print {
    body { background:#fff; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .cr-wrap { padding: 0; }
    .cr-section, .cr-hero, .cr-rec-card, .cr-action-card, .cr-theme-card, .cr-guardrail-card, .cr-outcome-card, .cr-flow-visual { break-inside: avoid; }
    .cr-signal-card { transition: none; }
    .cr-signal-card:hover { box-shadow: none; }
    .cr-data-table thead, .cr-evidence-rank-table thead { display: table-header-group; }
    .cr-table-wrap { overflow-x: visible; }
    .cr-advisor-text { max-height: none; }
    .cr-doc-footer { break-inside: avoid; }
  }
`;

// ── Section renderers ─────────────────────────────────────────────────────────

const reportTimestamp = () => new Date().toLocaleString(undefined, {
  year: 'numeric', month: 'short', day: 'numeric',
  hour: '2-digit', minute: '2-digit', hour12: true, timeZoneName: 'short',
});

const orgName = (pack) => String(pack?.organization?.profile?.company_name || '').trim();

const renderDocHeader = (modelName, reportType = 'Process Assessment Report', pack = null) => {
  const client = orgName(pack);
  return `
<div class="cr-doc-header">
  <div class="cr-doc-header-left">
    <div class="cr-doc-header-type">Process Advisor · ${esc(reportType)}</div>
    <div class="cr-doc-header-title">${esc(toReportTitleCase(modelName || 'Process Model'))}</div>
    ${client ? `<div class="cr-doc-header-time">Prepared for ${esc(client)}</div>` : ''}
  </div>
  <div class="cr-doc-header-time">${reportTimestamp()}</div>
</div>`;
};

const renderDocFooter = (pack = null) => {
  const client = orgName(pack);
  return `
<div class="cr-doc-footer">
  <div class="cr-doc-footer-brand">Process Advisor</div>
  <div class="cr-doc-footer-text">This assessment was generated with AI assistance from process mining evidence${client ? ` for ${esc(client)}` : ''} and should be validated by the accountable process owner before implementation. Generated ${reportTimestamp()}.</div>
</div>`;
};

const renderHero = (summary, position, pack) => {
  if (!summary && !position) return '';
  const am = getAss(position?.overall_assessment || '');
  const cm = getConf(position?.confidence || '');
  const scoreLabel = pack?.process_health_summary?.scoreLabel || '';
  return `
<div class="cr-hero">
  <div class="cr-hero-eyebrow">Executive Summary</div>
  <h1 class="cr-hero-headline">${esc(summary?.headline || '')}</h1>
  <div class="cr-hero-summary">${fmt(summary?.summary || '')}</div>
  <div class="cr-hero-meta">
    ${scoreLabel ? `<div class="cr-hero-position" style="background:${P.tint};color:${P.primaryDark};border:1px solid ${P.tintBorder};">Process Health ${esc(scoreLabel)}</div>` : ''}
    <div class="cr-hero-position" style="background:${am.bg};color:${am.color};">
      <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${am.bar};"></span>
      ${esc(position?.overall_assessment || '')}
    </div>
    <div class="cr-hero-position" style="background:${cm.bg};color:${cm.color};">
      Confidence: ${esc(position?.confidence || '')}
    </div>
  </div>
  ${position?.rationale ? `<div class="cr-hero-rationale">${fmt(position.rationale)}</div>` : ''}
</div>`;
};


const renderAssessmentScope = (scope) => {
  if (!scope || typeof scope !== 'object') return '';
  const footprint = Array.isArray(scope.data_footprint) ? scope.data_footprint.filter(i => i && (i.label || i.value)) : [];
  const assumptions = Array.isArray(scope.assumptions) ? scope.assumptions.filter(Boolean) : [];
  if (!scope.scope_summary && !footprint.length && !assumptions.length) return '';
  const footprintCards = footprint.map(item => `
    <div style="border:1px solid ${P.borderStrong};border-radius:10px;background:#fff;padding:12px 14px;">
      <div style="font-size:11px;font-weight:700;color:${P.muted};text-transform:uppercase;letter-spacing:.04em;margin-bottom:4px;">${esc(item.label || '')}</div>
      <div style="font-size:14px;font-weight:700;color:${P.ink};line-height:1.35;">${esc(item.value || '')}</div>
    </div>`).join('');
  const assumptionsHtml = assumptions.length ? `
    <div style="margin-top:14px;padding:12px 14px;border-radius:10px;background:${P.pageBg};border:1px solid ${P.borderStrong};">
      <div style="font-size:12px;font-weight:700;color:${P.text};margin-bottom:6px;">Assumptions and boundaries</div>
      <ul style="margin:0;padding-left:18px;color:${P.text};font-size:13px;line-height:1.55;">${assumptions.map(a => `<li>${fmt(a)}</li>`).join('')}</ul>
    </div>` : '';
  return `
<section class="cr-section">
  ${sectionHeader('scope', 'Assessment Scope', P.primary)}
  ${scope.scope_summary ? `<div style="font-size:14px;color:${P.text};line-height:1.6;margin-bottom:16px;">${fmt(scope.scope_summary)}</div>` : ''}
  ${footprint.length ? `<div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(170px,1fr));gap:10px;">${footprintCards}</div>` : ''}
  ${assumptionsHtml}
</section>`;
};

const renderSignals = (signals) => {
  if (!Array.isArray(signals) || !signals.length) return '';
  const cards = signals.map(s => {
    const sev = signalSeverity(s.label || '', s.value || '');
    return `
    <div class="cr-signal-card${sev ? ' ' + sev : ''}">
      <div class="cr-signal-top">
        <div class="cr-signal-label">${esc(toReportTitleCase(s.label || ''))}</div>
        ${srcBadge(s.source)}
      </div>
      <div class="cr-signal-value">${esc(s.value || '')}</div>
      <div class="cr-signal-why">${fmt(s.why_it_matters || '')}</div>
    </div>`;
  }).join('');
  return `
<div class="cr-section">
  ${sectionHeader('signals', 'Headline Signals', P.primary)}
  <div class="cr-signal-grid">${cards}</div>
</div>`;
};

const renderDiagnosticThemes = (themes) => {
  if (!Array.isArray(themes) || !themes.length) return '';
  const cards = themes.map((t, i) => {
    const accent = THEME_ACCENT_COLORS[i % THEME_ACCENT_COLORS.length];
    return `
    <div class="cr-theme-card" style="--accent:${accent};">
      <div class="cr-theme-header">
        <div class="cr-theme-num" style="background:${accent};">${i + 1}</div>
        <h3 class="cr-theme-name">${esc(t.theme || '')}</h3>
      </div>
      <div class="cr-theme-grid">
        <div class="cr-theme-panel">
          <div class="cr-theme-plabel evidence">Evidence</div>
          <div class="cr-theme-ptext">${fmt(t.evidence || '')}</div>
        </div>
        <div class="cr-theme-panel">
          <div class="cr-theme-plabel interpretation">Interpretation</div>
          <div class="cr-theme-ptext">${fmt(t.interpretation || '')}</div>
        </div>
        <div class="cr-theme-panel">
          <div class="cr-theme-plabel business">Business Effect</div>
          <div class="cr-theme-ptext">${fmt(t.business_effect || '')}</div>
        </div>
      </div>
    </div>`;
  }).join('');
  return `
<div class="cr-section">
  ${sectionHeader('diagnostic', 'Diagnostic Themes', P.danger)}
  ${cards}
</div>`;
};

const renderRecommendations = (recs) => {
  if (!Array.isArray(recs) || !recs.length) return '';
  const cards = recs.map((r, i) => {
    const pm = getPri(r.priority);
    const rt = getRT(r.recommendation_type);
    return `
    <div class="cr-rec-card">
      <div class="cr-rec-header-bar" style="border-top:3px solid ${pm.dot};">
        <div class="cr-rec-num-block">
          <div class="cr-rec-num-label">Rec</div>
          <div class="cr-rec-num-val">${i + 1}</div>
        </div>
        <div class="cr-rec-title-block">
          <h3 class="cr-rec-title">${esc(r.title || '')}</h3>
          <div class="cr-rec-badges">
            ${badge(r.priority || '', pm.bg, pm.color, pm.dot)}
            ${badge(r.recommendation_type || '', rt.bg, rt.color)}
          </div>
        </div>
      </div>
      <div class="cr-rec-body">
        <div class="cr-rec-cell">
          <div class="cr-rec-cell-label">Action</div>
          <div class="cr-rec-cell-text">${fmt(r.action || '')}</div>
        </div>
        <div class="cr-rec-cell">
          <div class="cr-rec-cell-label">Strategic Driver</div>
          <div class="cr-rec-cell-text">${fmt(r.strategic_driver || r.why_now || '')}</div>
        </div>
      </div>
      <div class="cr-rec-body-bottom">
        <div class="cr-rec-cell">
          <div class="cr-rec-cell-label benefit">Target / Success Measure</div>
          <div class="cr-rec-cell-text">${fmt(r.expected_benefit || '')}</div>
        </div>
        <div class="cr-rec-cell">
          <div class="cr-rec-cell-label owner">Owner</div>
          <div class="cr-rec-cell-text">${fmt(r.owner_hint || '')}</div>
        </div>
      </div>
    </div>`;
  }).join('');
  return `
<div class="cr-section">
  ${sectionHeader('recs', 'Priority Recommendations', P.warn)}
  ${cards}
</div>`;
};

const renderFutureState = (fss) => {
  if (!fss) return '';
  const principles = Array.isArray(fss.design_principles) ? fss.design_principles : [];
  const principleCards = principles.map((p) =>
    `<div class="cr-principle-card">
      <span style="width:22px;height:22px;border-radius:6px;background:${P.primary};color:#fff;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;">${SVG.check}</span>
      <span>${fmt(p)}</span>
    </div>`
  ).join('');
  return `
<div class="cr-section">
  ${sectionHeader('future', 'Future State Summary', P.primary)}
  <div style="font-size:16px;font-weight:700;color:${P.ink};margin-bottom:8px;">${esc(fss.headline || '')}</div>
  <div style="font-size:13px;line-height:1.7;color:${P.text};margin-bottom:${principleCards ? '16px' : '0'};">${fmt(fss.summary || '')}</div>
  ${principleCards ? `<div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing: .06em;color:${P.muted};margin-bottom:10px;">Design Principles</div><div class="cr-principles-grid">${principleCards}</div>` : ''}
</div>`;
};


const automationHowTo = (card, data, idx) => {
  const explicit = card.how_to || card.implementation_steps || card.implementation || card.action_plan || card.next_steps;
  if (explicit) return explicit;
  const candidates = Array.isArray(data?.automation_blueprint_summary?.automation_candidates) ? data.automation_blueprint_summary.automation_candidates : [];
  if (candidates[idx]) return candidates[idx];
  const title = String(card.title || '').toLowerCase();
  if (/closure|resolved|closed/.test(title)) return 'Define the closure trigger, confirm exception rules, configure a time-based Flow Designer action, test against recent incidents, then monitor reopened incidents and closure delay weekly.';
  if (/classif|routing|triage|assign/.test(title)) return 'Select high-quality training fields, validate assignment-group history, train the prediction model, pilot on a controlled queue, then measure first-time assignment accuracy and triage time.';
  if (/summari|agent/.test(title)) return 'Identify long-running or high-touch incidents, enable summarisation in the agent workspace, standardise generated notes, and review quality before broader rollout.';
  if (/virtual|deflect|self-service/.test(title)) return 'Rank repeatable incident types, design the virtual-agent conversation, connect knowledge articles and fulfilment actions, then track deflection and fallback rates.';
  return 'Convert the opportunity into a scoped experiment, define success metrics, assign an owner, implement in a pilot group, and review evidence before scaling.';
};

const enrichRoadmapPhase = (p, i) => {
  const parsed = parsePhase(p);
  let body = parsed.body || String(p || '');
  if (body.length < 120) {
    const additions = [
      'Prioritise the closure-delay fix, rework controls, ownership, and baseline KPI reporting so improvement is measurable from week one.',
      'Pilot intelligent triage and routing against high-quality classification data, then measure first-time assignment accuracy and triage elapsed time.',
      'Lock in standard paths, transition controls, SOPs, and governance routines so teams operate from a stable process baseline.',
      'Scale automation, virtual-agent deflection, and performance analytics once the workflow is stable enough for continuous optimisation.'
    ];
    body = `${body}${body ? ' — ' : ''}${additions[i] || 'Define owners, actions, measures, and governance checkpoints for this phase.'}`;
  }
  return { ...parsed, body };
};

const renderAutomation = (abs) => {
  if (!abs) return '';
  const candidates = Array.isArray(abs.automation_candidates) ? abs.automation_candidates : [];
  const items = candidates.map(c =>
    `<div class="cr-auto-item"><div class="cr-auto-icon">${SVG.bolt}</div><span>${fmt(c)}</span></div>`
  ).join('');
  return `
<div class="cr-section">
  ${sectionHeader('automation', 'Automation Blueprint Summary', P.accent)}
  <div style="font-size:16px;font-weight:700;color:${P.ink};margin-bottom:8px;">${esc(abs.headline || '')}</div>
  <div style="font-size:13px;line-height:1.7;color:${P.text};margin-bottom:${items ? '14px' : '0'};">${fmt(abs.summary || '')}</div>
  ${items ? `<div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing: .06em;color:${P.muted};margin-bottom:8px;">Automation Candidates</div><div class="cr-auto-grid">${items}</div>` : ''}
</div>`;
};

const renderRoadmap = (irs) => {
  if (!irs) return '';
  const phases = Array.isArray(irs.roadmap_phases) ? irs.roadmap_phases : [];
  const phaseItems = phases.map((p, i) => {
    const col = PHASE_COLORS[i % PHASE_COLORS.length];
    const parsed = enrichRoadmapPhase(p, i);
    return `
    <div class="cr-phase">
      <div class="cr-phase-circle" style="background:${col};">${i + 1}</div>
      <div class="cr-phase-text">
        ${parsed.label ? `<strong style="color:${col};">${esc(parsed.label)}</strong>` : ''}
        ${esc(parsed.body)}
      </div>
    </div>`;
  }).join('');
  return `
<div class="cr-section">
  ${sectionHeader('roadmap', 'Implementation Roadmap Summary', P.warn)}
  <div style="font-size:16px;font-weight:700;color:${P.ink};margin-bottom:8px;">${esc(irs.headline || '')}</div>
  <div style="font-size:13px;line-height:1.7;color:${P.text};margin-bottom:${phaseItems ? '14px' : '0'};">${fmt(irs.summary || '')}</div>
  ${phaseItems ? `<div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing: .06em;color:${P.muted};margin-bottom:8px;">Phases</div><div class="cr-phases">${phaseItems}</div>` : ''}
</div>`;
};

const renderOutcomes = (outcomes) => {
  if (!Array.isArray(outcomes) || !outcomes.length) return '';
  const cards = outcomes.map(o => {
    const d = getDir(o.target_direction || 'Improve');
    return `
    <div class="cr-outcome-card">
      <div class="cr-outcome-dir-chip" style="background:${d.bg};color:${d.color};">
        <span style="font-weight:700;">${d.icon}</span> ${esc(o.target_direction || '')}
      </div>
      <div class="cr-outcome-text">${esc(o.outcome || '')}</div>
      <div class="cr-outcome-why">${fmt(o.why_it_matters || '')}</div>
    </div>`;
  }).join('');
  return `
<div class="cr-section">
  ${sectionHeader('outcomes', 'Expected Outcomes', P.primaryAlt)}
  <div class="cr-outcome-grid">${cards}</div>
</div>`;
};

const renderGuardrails = (guardrails) => {
  if (!Array.isArray(guardrails) || !guardrails.length) return '';
  const cards = guardrails.map((g) =>
    `<div class="cr-guardrail-card">
      <div class="cr-guardrail-header">
        <span class="cr-guardrail-icon">${SVG.shield}</span>
        <h3 class="cr-guardrail-title">${esc(g.title || '')}</h3>
      </div>
      <div class="cr-guardrail-desc">${fmt(g.description || '')}</div>
    </div>`
  ).join('');
  return `
<div class="cr-section">
  ${sectionHeader('guardrails', 'Governance Guardrails', P.warn)}
  <div class="cr-guardrail-grid">${cards}</div>
</div>`;
};


const nonEmptyRows = (rows) => Array.isArray(rows) ? rows.filter(r => r && typeof r === 'object' && Object.values(r).some(v => v !== undefined && v !== null && String(v).trim() !== '')) : [];
const formatCellValue = (v) => {
  if (v === undefined || v === null) return '';
  if (Array.isArray(v)) return v.map(formatCellValue).filter(Boolean).join('; ');
  if (typeof v === 'object') {
    const label = v.label || v.name || v.value || v.key || v.displayValue || v.display_value || '';
    const count = v.count || v.tasks || v.workItems || v.work_items || v.total || v.volume || '';
    const pct = v.percentage || v.percent || v.pct || v.share || '';
    return [label, count, pct].filter(Boolean).join(' · ') || JSON.stringify(v);
  }
  return String(v);
};
const short = (v, max = 120) => {
  const s = formatCellValue(v);
  return s.length > max ? `${s.slice(0, max - 1).replace(/\s+\S*$/, '').trim()}…` : s;
};
const sentenceShort = (v, max = 900) => truncateAtSentence(formatCellValue(v), max);
const rowValue = (row, keys = []) => {
  for (const k of keys) if (row?.[k] !== undefined && row?.[k] !== null && String(row[k]).trim() !== '') return row[k];
  return '';
};
const numeric = (v) => {
  const n = Number(String(v ?? '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
};


const fmtNumber = (v, decimals = 0) => {
  const n = numeric(v);
  if (!Number.isFinite(n)) return esc(v || '');
  return n.toLocaleString(undefined, { maximumFractionDigits: decimals, minimumFractionDigits: decimals && Math.abs(n % 1) > 0 ? Math.min(decimals, 2) : 0 });
};
const pctValue = (row) => rowValue(row, ['% Total TASKS', '% Total Tasks', 'Percent', 'Percentage', 'pct', 'share']);
// Format a percentage value for display: appends % when the source value is a bare number.
const fmtPctDisplay = (v, decimals = 1) => {
  const s = String(v ?? '').trim();
  if (!s) return '';
  if (s.includes('%')) return s;
  const n = Number(s.replace(/[^0-9.\-]/g, ''));
  if (!Number.isFinite(n)) return s;
  return `${n.toFixed(Math.abs(n % 1) > 0.0001 ? decimals : 0)}%`;
};
const transitionName = (row) => rowValue(row, ['Transition']) || [rowValue(row, ['From Activity']), rowValue(row, ['To Activity'])].filter(Boolean).join(' → ');
const getRowSignal = (row, mode = 'general') => {
  const rework = String(rowValue(row, ['Is rework?', 'Is Rework', 'isRework'])).toLowerCase() === 'true';
  const avg = numeric(rowValue(row, ['Avg deviation (hrs)', 'Avg Duration (hrs)', 'Avg Duration']));
  const vol = numeric(rowValue(row, ['Work items', 'TASKS', 'Tasks', 'count']));
  if (mode === 'variant') return rework ? 'Contains rework or loop behaviour' : (pctValue(row) ? `${fmtPctDisplay(pctValue(row))} of work items` : 'Common pathway');
  const from = String(rowValue(row, ['From Activity'])).trim().toLowerCase();
  const to = String(rowValue(row, ['To Activity'])).trim().toLowerCase();
  if (from && to && from === to) return 'Self-transition / status churn';
  if (avg >= 24) return `Bottleneck candidate (${fmtNumber(avg, 1)}h avg)${rework ? ' · loop signal' : ''}`;
  if (rework) return 'Loop / regressive signal';
  if (vol > 0) return 'High-volume improvement lever';
  return 'Evidence signal';
};

const prettyColumn = (c) => String(c || '')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, ch => ch.toUpperCase());

const renderDataTable = (title, rows, preferredColumns = [], maxRows = 12, note = '') => {
  const cleanRows = nonEmptyRows(rows).slice(0, maxRows);
  if (!cleanRows.length) return '';
  const allKeys = Array.from(new Set(cleanRows.flatMap(r => Object.keys(r))));
  const cols = (preferredColumns.length ? preferredColumns.filter(c => allKeys.includes(c)) : allKeys).slice(0, 7);
  if (!cols.length) return '';
  return `
  <div class="cr-evidence-table-title"><span>${esc(title)}</span><span>${cleanRows.length} shown</span></div>
  ${note ? `<div class="cr-interpretation">${fmt(note)}</div>` : ''}
  <div class="cr-table-wrap"><table class="cr-data-table"><thead><tr>${cols.map(c => `<th scope="col">${esc(prettyColumn(c))}</th>`).join('')}</tr></thead><tbody>
    ${cleanRows.map(r => `<tr>${cols.map(c => c === 'topValues' ? `<td>${formatAttributeTopValuesHtml(r[c])}</td>` : `<td>${esc(short(r[c], 180))}</td>`).join('')}</tr>`).join('')}
  </tbody></table></div>`;
};

const renderRankedEvidenceTable = (title, rows, mode = 'transition', maxRows = 10, note = '') => {
  const cleanRows = nonEmptyRows(rows).slice(0, maxRows);
  if (!cleanRows.length) return '';
  const isVariant = mode === 'variant';
  const tableRows = cleanRows.map((r, i) => {
    const item = isVariant ? (rowValue(r, ['Variant', 'Path', 'variant']) || `Variant ${i + 1}`) : (transitionName(r) || `Transition ${i + 1}`);
    const volume = rowValue(r, ['Work items', 'TASKS', 'Tasks', 'count']);
    const avg = rowValue(r, ['Avg deviation (hrs)', 'Avg Duration (hrs)', 'Avg Duration']);
    const pct = pctValue(r);
    const secondary = isVariant
      ? [volume ? `${fmtNumber(volume)} work items` : '', pct ? `${fmtPctDisplay(pct)} of total` : '', rowValue(r, ['Avg Duration (hrs)', 'Avg Duration']) ? `${fmtNumber(rowValue(r, ['Avg Duration (hrs)', 'Avg Duration']), 1)}h avg duration` : ''].filter(Boolean).join(' · ')
      : [rowValue(r, ['From Activity']) && rowValue(r, ['To Activity']) ? `${rowValue(r, ['From Activity'])} → ${rowValue(r, ['To Activity'])}` : '', volume ? `${fmtNumber(volume)} work items` : '', avg ? `${avg}h avg` : ''].filter(Boolean).join(' · ');
    const primaryMetric = isVariant ? (volume || pct || '') : (avg || volume || '');
    const metricSuffix = !isVariant && avg ? 'h' : '';
    return `<tr>
      <td>${i + 1}</td>
      <td><div class="cr-evidence-main">${esc(short(item, 160))}</div>${secondary ? `<div class="cr-evidence-sub">${esc(secondary)}</div>` : ''}</td>
      <td>${primaryMetric ? `<span class="cr-num-pill">${esc(fmtNumber(primaryMetric, !isVariant && avg ? 1 : 0))}${metricSuffix}</span>` : ''}</td>
      <td><span class="cr-signal-pill">${esc(getRowSignal(r, mode))}</span></td>
    </tr>`;
  }).join('');
  return `<div class="cr-evidence-table-title"><span>${esc(title)}</span><span>Top ${cleanRows.length}</span></div>${note ? `<div class="cr-interpretation">${fmt(note)}</div>` : ''}
  <div class="cr-table-wrap"><table class="cr-evidence-rank-table"><thead><tr><th scope="col">#</th><th scope="col">Evidence item</th><th scope="col">Metric</th><th scope="col">Signal</th></tr></thead><tbody>${tableRows}</tbody></table></div>`;
};

const renderBarList = (title, rows, labelKeys, valueKeys, maxRows = 10, suffix = '', note = '') => {
  const isVariant = /variant/i.test(title);
  return renderRankedEvidenceTable(title, rows, isVariant ? 'variant' : 'transition', maxRows, note);
};


// Strip score/confidence/verdict boilerplate that duplicates the score card next to the excerpt.
const cleanHealthExcerpt = (text = '', verdict = '') => {
  let s = String(text || '').replace(/\s+/g, ' ').trim();
  const v = String(verdict || '').replace(/[.\s]+$/, '').trim();
  if (v.length > 20) s = s.split(v).join(' ');
  s = s
    .replace(/\d+(?:\.\d+)?\s*(?:out of|\/)\s*10\s*(?:Process\s*)?Health\s*Score\s*(?:Weak|Poor|Fair|Moderate|Good|Strong|Critical)?/gi, ' ')
    .replace(/(?:✓\s*)?Confidence:\s*(?:High|Moderate|Low)/gi, ' ')
    .replace(/^Process Health\s*[—-]?\s*/i, '')
    .replace(/\s+/g, ' ')
    .trim();
  return s;
};

const renderProcessHealthSummary = (pack) => {
  const health = pack?.process_health_summary || {};
  if (!health?.available && !health?.scoreLabel && !health?.excerpt) return '';
  const metrics = health.metrics || {};
  const metricRows = [
    ['Work items', metrics.workItems !== undefined && metrics.workItems !== '' ? fmtNumber(metrics.workItems) : ''],
    ['Variants', metrics.variants !== undefined && metrics.variants !== '' ? fmtNumber(metrics.variants) : ''],
    ['Activities', metrics.activities],
    ['Transitions', metrics.transitions],
    ['Avg transitions', metrics.avgTransitions],
    ['Avg duration (hrs)', metrics.avgDurationHours !== undefined && metrics.avgDurationHours !== '' ? fmtNumber(metrics.avgDurationHours, 1) : ''],
    ['Rework rate', fmtPctDisplay(metrics.reworkPercent)],
    ['Flow efficiency', fmtPctDisplay(metrics.flowEfficiency)],
    ['Top bottleneck', metrics.topBottleneck],
    ['Top variant coverage', fmtPctDisplay(metrics.topVariantCoverage)],
    ['Largest rework loop', metrics.largestReworkLoop],
  ].filter(([, v]) => v !== undefined && v !== null && String(v).trim() !== '');
  const metricCards = metricRows.map(([label, value]) => `<div class="cr-health-metric"><div class="cr-health-metric-label">${esc(label)}</div><div class="cr-health-metric-value">${esc(value)}</div></div>`).join('');
  return `<div class="cr-section">
    ${sectionHeader('signals', 'Process Health Summary', P.primary)}
    <div class="cr-evidence-intro">This summary provides the overall assessment score, confidence, key drivers, and supporting process metrics.</div>
    <div class="cr-health-panel">
      <div class="cr-health-score-card">
        <div class="cr-health-score">${esc(health.scoreLabel || health.score || '—')}</div>
        <div class="cr-health-score-label">Process Health Score</div>
        ${health.confidence ? `<div class="cr-hero-position" style="margin-top:10px;background:${P.tint};color:${P.primary};">Confidence: ${esc(health.confidence)}</div>` : ''}
        ${health.verdict ? `<div class="cr-health-verdict">${fmt(cleanSentenceEnd(health.verdict))}</div>` : ''}
      </div>
      <div>
        <div class="cr-health-metrics">${metricCards}</div>
        ${cleanHealthExcerpt(health.excerpt, health.verdict) ? `<div class="cr-health-excerpt"><strong>Process Health AI summary:</strong><br/>${fmt(truncateAtSentence(cleanHealthExcerpt(health.excerpt, health.verdict), 900))}</div>` : ''}
      </div>
    </div>
  </div>`;
};

const renderCompiledSnapshot = (pack) => {
  const snapshot = Array.isArray(pack?.process_snapshot) ? pack.process_snapshot : [];
  if (!snapshot.length) return '';
  const cards = snapshot.map(item => `<div class="cr-evidence-card"><div class="cr-evidence-label">${esc(item.label || '')}</div><div class="cr-evidence-value">${esc(item.value || '')}</div></div>`).join('');
  return `<div class="cr-section">${sectionHeader('signals', 'Process Snapshot', P.primary)}<div class="cr-evidence-grid">${cards}</div></div>`;
};

const renderProcessMapEvidence = (pack) => {
  const map = pack?.process_map || {};
  const nodes = Array.isArray(map.nodes) ? map.nodes : [];
  const edges = Array.isArray(map.edges) ? map.edges : [];
  if (!nodes.length && !edges.length) return '';

  const norm = (v) => String(v || '').trim().toLowerCase();
  const findEdge = (from, to) => edges.find(e => norm(e.from) === norm(from) && norm(e.to) === norm(to)) || {};
  const metric = (e) => {
    const bits = [];
    if (e.volume) bits.push(`${e.volume} items`);
    if (e.avgHours) bits.push(`${e.avgHours}h avg`);
    return bits.join(' · ') || 'No direct transition evidence';
  };

  const startToWip = findEdge('Start', 'Work in Progress');
  const wipToCompleted = findEdge('Work in Progress', 'Completed');
  const completedToClosed = findEdge('Completed', 'Closed');
  const wipToPending = findEdge('Work in Progress', 'Pending');
  const pendingToWip = findEdge('Pending', 'Work in Progress');
  const wipToOpen = findEdge('Work in Progress', 'Open');
  const openToWip = findEdge('Open', 'Work in Progress');

  const ghostTitle = completedToClosed.avgHours
    ? `Completed → Closed averages ${esc(completedToClosed.avgHours)} hours`
    : 'Completed → Closed is a major administrative checkpoint';

  const transitionCards = edges.slice(0, 8).map(e => `<div class="cr-transition-card">
      <div class="cr-transition-path">${esc(e.from)} → ${esc(e.to)}</div>
      <div class="cr-transition-meta">${esc(e.volume || '—')} items${e.avgHours ? ` · ${esc(e.avgHours)}h average` : ''}${e.isRework ? ' · rework / loop signal' : ''}</div>
    </div>`).join('');

  return `<div class="cr-section">${sectionHeader('diagnostic', 'Workflow Evidence', P.primaryAlt)}
    <div class="cr-evidence-intro">This view translates the transition evidence into the operating story of the workflow: the likely core path, administrative delay points, and the main rework loops that should be investigated.</div>

    <div class="cr-flow-visual">
      <div class="cr-section-kicker">Core path evidence</div>
      <div class="cr-flow-main">
        <div class="cr-flow-node">Start</div><div class="cr-flow-arrow">→</div>
        <div class="cr-flow-node">Work in Progress</div><div class="cr-flow-arrow">→</div>
        <div class="cr-flow-node">Completed</div><div class="cr-flow-arrow">→</div>
        <div class="cr-flow-node end">Closed</div>
      </div>
      <div class="cr-flow-edge-note">
        <div class="cr-flow-note"><strong>Start → Work in Progress</strong><br/>${esc(metric(startToWip))}</div>
        <div class="cr-flow-note"><strong>Work in Progress → Completed</strong><br/>${esc(metric(wipToCompleted))}</div>
        <div class="cr-flow-note"><strong>Completed → Closed</strong><br/>${esc(metric(completedToClosed))}</div>
      </div>
    </div>

    <div class="cr-flow-alert">
      <div class="cr-flow-alert-icon">!</div>
      <div><div class="cr-flow-alert-title">Ghost bottleneck: ${ghostTitle}</div>
      <div class="cr-flow-alert-copy">The largest delay appears after the work is already marked completed. This can make operational performance look much worse than technical resolution performance and is a strong candidate for auto-close rules, customer confirmation policy, or closure governance.</div></div>
    </div>

    <div class="cr-loop-grid">
      <div class="cr-loop-card"><div class="cr-loop-title">Waiting loop: Work in Progress ↔ Pending</div><div class="cr-loop-copy">Work moves from active handling into waiting and back again. Evidence: WIP → Pending ${esc(metric(wipToPending))}; Pending → WIP ${esc(metric(pendingToWip))}. This usually points to missing information, dependency waits, or unclear handoff criteria.</div></div>
      <div class="cr-loop-card"><div class="cr-loop-title">Regressive cycle: Work in Progress ↔ Open</div><div class="cr-loop-copy">Work returns from active handling to the intake/open queue. Evidence: WIP → Open ${esc(metric(wipToOpen))}; Open → WIP ${esc(metric(openToWip))}. This is a stronger rework signal because it suggests triage, routing, or qualification failure.</div></div>
    </div>

    <div class="cr-evidence-table-title"><span>Highest-volume transition evidence</span><span>${Math.min(edges.length, 8)} shown</span></div>
    <div class="cr-transition-compact">${transitionCards}</div>
  </div>`;
};

const renderEvidenceTables = (pack) => {
  const tables = pack?.evidence_tables || {};
  const parts = [
    renderBarList('Top variants by work item volume', tables.top_variants, ['Variant', 'Path', 'variant'], ['Work items', 'TASKS', 'Tasks'], 10, '', 'Shows the dominant paths and how concentrated or fragmented the process is. Low coverage by the top path usually indicates exception handling, inconsistent practice, or uncontrolled variation.'),
    renderBarList('Slowest transitions by average hours', tables.slowest_transitions, ['Transition'], ['Avg deviation (hrs)', 'Avg Duration (hrs)'], 10, '', 'Ranks the transitions creating the greatest elapsed-time drag. These are high-value candidates for bottleneck review, automation, SLA tuning, or process redesign.'),
    renderBarList('Highest-volume transitions', tables.highest_volume_transitions, ['Transition'], ['Work items', 'Tasks'], 10, '', 'Highlights the paths where small improvements can have broad operational impact because they touch the highest number of work items.'),
    renderDataTable('Rework / loop transitions', tables.rework_transitions, ['Transition', 'From Activity', 'To Activity', 'Work items', 'Avg deviation (hrs)', 'Is rework?'], 12, 'Rework and loop transitions identify where work moves backwards or cycles between activities. These are strong signals for triage defects, unclear ownership, poor handoffs, or missing information.'),
    renderDataTable('Metrics detail', tables.metrics, Object.keys((tables.metrics || [])[0] || {}), 8, 'Baseline metrics provide the numerical context for the assessment and should be used to validate the narrative recommendations.'),
    renderDataTable('Field / attribute evidence', tables.field_usage, ['field', 'type', 'totalTasks', 'coverage', 'missing', 'topValues'], 12, 'Attribute evidence shows whether segmentation, routing, classification and automation recommendations are supported by reliable data. Top values are formatted from the source distribution rather than raw objects.'),
  ].filter(Boolean).join('');
  if (!parts) return '';
  return `<div class="cr-section">${sectionHeader('signals', 'Detailed Process Evidence', P.primary)}<div class="cr-evidence-intro">The following evidence highlights the process patterns, bottlenecks, rework signals, and attribute patterns supporting the assessment.</div>${parts}</div>`;
};



const cleanAdvisorSentence = (text = '', max = 180) => {
  let s = String(text || '')
    .replace(/Generated\s+[A-Z][a-z]{2}\s+\d{1,2},\s+\d{4}[^.]{0,80}/gi, ' ')
    .replace(/Quick Healthcheck\s+[—-]\s+[^\n.]{0,120}/gi, ' ')
    .replace(/Process Health\s+[—-]\s+[^\n.]{0,120}/gi, ' ')
    .replace(/AI Opportunities\s+[—-]\s+[^\n.]{0,120}/gi, ' ')
    .replace(/Execution Strategy\s+[—-]\s+[^\n.]{0,120}/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const sentences = s.split(/(?<=[.!?])\s+/).filter(Boolean);
  const chosen = sentences.find(x => x.length > 40 && !/^\d+(\.\d+)?\s*(\/10|out of 10)/i.test(x)) || sentences[0] || s;
  return sentenceShort(chosen, max);
};

const normaliseSynthesisSource = (item = {}) => item.source_section || item.source || item.section || 'Advisor';
const normaliseSynthesisPoint = (item = {}) => item.advisory_point || item.point || item.insight || item.title || '';

const isAutomationSynthesis = (item = {}) => /opportunit|automation|flow designer|predictive|assignment|virtual agent|ai/i.test([
  normaliseSynthesisSource(item),
  normaliseSynthesisPoint(item),
  item.evidence_basis,
  item.assessment_relevance,
].join(' '));

const extractAutomationOpportunityCards = (data = {}, pack = {}) => {
  const explicit = Array.isArray(data?.ai_automation_opportunities) ? data.ai_automation_opportunities : [];
  if (explicit.length) {
    return explicit.map(item => ({
      source: item.source || 'AI, Automation & Optimisation',
      title: item.title || item.opportunity || item.advisory_point || '',
      evidence: item.evidence_basis || item.evidence || '',
      implication: item.assessment_relevance || item.impact || item.recommendation || '',
    })).filter(x => x.title || x.evidence || x.implication).slice(0, 6);
  }

  const synth = Array.isArray(data?.advisor_synthesis) ? data.advisor_synthesis : [];
  const fromSynth = synth.filter(isAutomationSynthesis).map(item => ({
    source: normaliseSynthesisSource(item),
    title: normaliseSynthesisPoint(item),
    evidence: item.evidence_basis || '',
    implication: item.assessment_relevance || '',
  })).filter(x => x.title || x.evidence || x.implication);
  if (fromSynth.length) return fromSynth.slice(0, 6);

  const opp = (pack?.advisor_outputs || []).find(o => o?.key === 'opportunities' || /opportunit/i.test(o?.label || ''));
  if (!opp?.text) return [];
  const text = String(opp.text).replace(/\s+/g, ' ').trim();
  const chunks = text.split(/(?=Opportunity\s+\d+\b|Automate\b|Improve\b|Use\b|Apply\b)/i).filter(x => x.trim().length > 30);
  const cards = chunks.slice(0, 4).map(chunk => {
    const titleMatch = chunk.match(/(?:Opportunity\s+\d+\s*)?([^.!?]{20,95})/i);
    return {
      source: 'AI, Automation & Optimisation',
      title: cleanAdvisorSentence(titleMatch?.[1] || chunk, 120),
      evidence: cleanAdvisorSentence(chunk, 190),
      implication: 'Use this Advisor opportunity to shape automation, routing, or governance actions in the improvement roadmap.',
    };
  }).filter(x => x.title || x.evidence);
  if (cards.length) return cards;

  return [{
    source: 'AI, Automation & Optimisation',
    title: cleanAdvisorSentence(text, 140) || 'AI-enabled process improvement opportunity',
    evidence: 'Generated from the AI, Automation & Optimisation Opportunity Hub and cross-checked against process evidence.',
    implication: 'Use this opportunity to inform the automation roadmap and recommended actions.',
  }];
};

const renderAIAutomationOpportunities = (data, pack) => {
  const cards = extractAutomationOpportunityCards(data, pack);
  const blueprint = data?.automation_blueprint_summary || {};
  const candidates = Array.isArray(blueprint.automation_candidates) ? blueprint.automation_candidates : [];
  if (!cards.length && !blueprint.summary && !candidates.length) return '';
  const intro = blueprint.summary || 'Advisor opportunities are translated into practical automation and AI improvement candidates, grounded in the same evidence used for findings and actions.';
  const headline = blueprint.headline || 'Practical AI and automation roadmap';
  return `<div class="cr-section">${sectionHeader('automation', 'AI & Automation Opportunities', P.accent)}
    <div style="font-size:16px;font-weight:700;color:${P.ink};margin-bottom:8px;">${esc(headline)}</div>
    <div class="cr-evidence-intro">${fmt(intro)}</div>
    <div class="cr-advisor-synthesis-grid">${cards.map((card, i) => `<div class="cr-advisor-synthesis-card">
      <div class="cr-advisor-source">${esc(card.source || `Opportunity ${i + 1}`)}</div>
      <div class="cr-advisor-point">${fmt(card.title || '')}</div>
      ${card.evidence ? `<div class="cr-advisor-basis"><strong>Evidence basis:</strong> ${fmt(card.evidence)}</div>` : ''}
      <div class="cr-advisor-relevance"><strong>How to implement:</strong> ${fmt(automationHowTo(card, data, i))}</div>
      ${card.implication ? `<div class="cr-advisor-basis"><strong>Assessment relevance:</strong> ${fmt(card.implication)}</div>` : ''}
    </div>`).join('')}</div>
    ${candidates.length && !cards.length ? `<div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing: .06em;color:${P.muted};margin:14px 0 8px;">Automation Candidates</div><div class="cr-auto-grid">${candidates.map(c => `<div class="cr-auto-item"><div class="cr-auto-icon">${SVG.bolt}</div><span>${fmt(c)}</span></div>`).join('')}</div>` : ''}
  </div>`;
};

const renderAdvisorSynthesis = (data, pack) => {
  const synth = Array.isArray(data?.advisor_synthesis) ? data.advisor_synthesis : [];
  const filtered = synth.filter(item => !isAutomationSynthesis(item));
  if (!filtered.length) return '';
  const cards = filtered.slice(0, 6).map(item => `<div class="cr-advisor-synthesis-card">
    <div class="cr-advisor-source">${esc(normaliseSynthesisSource(item))}</div>
    <div class="cr-advisor-point">${fmt(normaliseSynthesisPoint(item))}</div>
    ${item.evidence_basis ? `<div class="cr-advisor-basis"><strong>Evidence basis:</strong> ${fmt(item.evidence_basis)}</div>` : ''}
    ${item.assessment_relevance ? `<div class="cr-advisor-relevance"><strong>Assessment relevance:</strong> ${fmt(item.assessment_relevance)}</div>` : ''}
  </div>`).join('');
  return `<div class="cr-section">${sectionHeader('note', 'Advisor Insight Summary', P.primaryDark)}
    <div class="cr-evidence-intro">Key Advisor insights consolidated into the assessment narrative.</div>
    <div class="cr-advisor-synthesis-grid">${cards}</div>
  </div>`;
};

const renderAdvisorOutputs = () => '';


const renderCuratedAssessmentItems = (pack) => {
  const findings = Array.isArray(pack?.curated_findings) ? pack.curated_findings : [];
  const actions = Array.isArray(pack?.curated_actions) ? pack.curated_actions : [];
  if (!findings.length && !actions.length) return '';

  const findingCards = findings.slice(0, 12).map((f, i) => {
    const evidence = f.evidence || f.evidenceSummary || f.notes || '';
    const recommendation = f.recommendation || f.action || '';
    return `<div class="cr-action-card">
      <div class="cr-action-head"><div class="cr-action-num">${i + 1}</div><div style="flex:1;min-width:0;"><h3 class="cr-action-title">${esc(f.title || f.finding || `Finding ${i + 1}`)}</h3><div class="cr-curated-meta" style="margin-top:7px;">${[f.severity, f.confidence, f.status, f.area].filter(Boolean).slice(0,4).map(v => `<span class="cr-small-chip">${esc(v)}</span>`).join('')}</div></div></div>
      <div class="cr-action-body">
        ${evidence ? `<div><div class="cr-action-label">Evidence</div><div class="cr-action-copy">${fmt(evidence)}</div></div>` : ''}
        ${recommendation ? `<div class="cr-linked-note"><strong>Recommended response:</strong> ${fmt(recommendation)}</div>` : ''}
      </div>
    </div>`;
  }).join('');

  const actionCards = actions.slice(0, 12).map((a, i) => {
    const steps = a.implementationSteps || a.action || a.notes || '';
    const benefit = a.successMeasure || '';
    const evidence = a.evidenceSummary || '';
    const chips = [
      a.priority ? `Priority: ${a.priority}` : '',
      a.expectedValue ? `Value: ${a.expectedValue}` : '',
      a.effort ? `Effort: ${a.effort}` : '',
      a.owner || '',
      a.status || '',
    ].filter(Boolean).slice(0, 5);
    return `<div class="cr-action-card">
      <div class="cr-action-head"><div class="cr-action-num">${i + 1}</div><div style="flex:1;min-width:0;"><h3 class="cr-action-title">${esc(a.title || `Action ${i + 1}`)}</h3><div class="cr-curated-meta" style="margin-top:7px;">${chips.map(v => `<span class="cr-small-chip">${esc(v)}</span>`).join('')}</div></div></div>
      <div class="cr-action-body">
        ${steps ? `<div><div class="cr-action-label">Action / implementation</div><div class="cr-action-copy">${fmt(steps)}</div></div>` : ''}
        ${evidence ? `<div><div class="cr-action-label">Evidence basis</div><div class="cr-action-copy">${fmt(evidence)}</div></div>` : ''}
        ${benefit ? `<div class="cr-linked-note"><strong>Target / Success Measure:</strong> ${fmt(benefit)}</div>` : ''}
      </div>
    </div>`;
  }).join('');

  return `<div class="cr-section">${sectionHeader('recs', 'Curated Findings', P.warn)}
    ${findings.length ? `<div class="cr-evidence-intro">The following findings represent the highest-priority improvement areas identified from the assessment evidence.</div><div class="cr-action-grid">${findingCards}</div>` : `<div class="cr-interpretation">No curated findings were saved before report generation.</div>`}
  </div>
  <div class="cr-section">${sectionHeader('roadmap', 'Curated Improvement Actions', P.primary)}
    ${actions.length ? `<div class="cr-evidence-intro">Consultant-curated actions from the assessment workbench, translating the findings above into practical improvement work for client follow-up. AI-generated priority recommendations appear later in this report.</div><div class="cr-action-grid">${actionCards}</div>` : `<div class="cr-interpretation">No curated actions were saved before report generation.</div>`}
  </div>`;
};

const renderValueItems = (pack) => {
  const items = Array.isArray(pack?.value_items) ? pack.value_items : [];
  if (!items.length) return '';
  // Suppress value items that simply repeat curated findings/actions rendered earlier in the report.
  const curatedTitles = new Set([
    ...(Array.isArray(pack?.curated_findings) ? pack.curated_findings : []),
    ...(Array.isArray(pack?.curated_actions) ? pack.curated_actions : []),
  ].map(x => String(x?.title || x?.finding || '').trim().toLowerCase()).filter(Boolean));
  const distinct = items.filter(i => !curatedTitles.has(String(i?.title || '').trim().toLowerCase()));
  if (!distinct.length) return '';
  const cards = distinct.map(i => `<div class="cr-outcome-card"><div class="cr-outcome-dir-chip" style="background:${P.tint};color:${P.primary};">Value</div><div class="cr-outcome-text">${esc(i.title || i.expectedValue || 'Value item')}</div><div class="cr-outcome-why">${fmt(i.evidence || i.action || i.notes || '')}</div></div>`).join('');
  return `<div class="cr-section">${sectionHeader('outcomes', 'Value Analysis and Benefit Evidence', P.primaryAlt)}<div class="cr-outcome-grid">${cards}</div></div>`;
};

const renderEvidenceAppendix = (pack) => {
  const tables = pack?.evidence_tables || {};
  const include = pack?.selected_options?.includeEvidenceAppendix !== false;
  if (!include) return '';
  const parts = [
    renderDataTable('Appendix — top variants', tables.top_variants, ['Variant', 'ID', 'Work items', '% Total TASKS', 'Is Rework', 'Loops', 'Avg Duration (hrs)'], 15),
    renderDataTable('Appendix — slowest transitions', tables.slowest_transitions, ['Transition', 'From Activity', 'To Activity', 'Work items', 'Avg deviation (hrs)', 'Std deviation (hrs)', 'Is rework?'], 15),
    renderDataTable('Appendix — highest-volume transitions', tables.highest_volume_transitions, ['Transition', 'From Activity', 'To Activity', 'Work items', 'Avg deviation (hrs)', 'Is rework?'], 15),
    renderDataTable('Appendix — status/activity mappings', tables.status_type_mappings, Object.keys((tables.status_type_mappings || [])[0] || {}), 20),
    renderAdvisorOutputs(pack),
  ].filter(Boolean).join('');
  return parts ? `<div class="cr-section">${sectionHeader('scope', 'Evidence Appendix', P.muted)}${parts}</div>` : '';
};

const renderNote = (note) => {
  if (!note) return '';
  return `
<div class="cr-section">
  ${sectionHeader('note', 'Consultant Note', P.warn)}
  <div class="cr-note">
    <div class="cr-note-icon">${SVG.note}</div>
    <div class="cr-note-text">${fmt(note)}</div>
  </div>
</div>`;
};

// ── Main export ───────────────────────────────────────────────────────────────
// Section order follows the consulting arc: verdict → scorecard → scope → signals →
// findings/themes → recommendations → future state/roadmap → governance → supporting
// evidence → note → appendix. Raw evidence sits after the narrative, not inside it.
const buildReportBody = (data) => [
  renderHero(data.report_summary, data.executive_position, data.compiled_evidence_pack),
  renderProcessHealthSummary(data.compiled_evidence_pack),
  renderAssessmentScope(data.assessment_scope),
  renderSignals(data.headline_signals),
  renderCuratedAssessmentItems(data.compiled_evidence_pack),
  renderDiagnosticThemes(data.diagnostic_themes),
  renderRecommendations(data.priority_recommendations),
  renderAIAutomationOpportunities(data, data.compiled_evidence_pack),
  renderAdvisorSynthesis(data, data.compiled_evidence_pack),
  renderFutureState(data.future_state_summary),
  renderRoadmap(data.implementation_roadmap_summary),
  renderOutcomes(data.expected_outcomes),
  renderGuardrails(data.governance_guardrails),
  renderProcessMapEvidence(data.compiled_evidence_pack),
  renderEvidenceTables(data.compiled_evidence_pack),
  renderValueItems(data.compiled_evidence_pack),
  renderNote(data.consultant_note),
  renderEvidenceAppendix(data.compiled_evidence_pack),
];

// Post-process pass: assign sequential numbers + anchor ids to every section
// header (marked by data-toc), and return the ordered list of titles for the TOC.
const numberSections = (body) => {
  const titles = [];
  const numbered = body.replace(
    /<h2 class="cr-section-title" data-toc="([^"]*)">([^<]*)<\/h2>/g,
    (match, tocTitle, text) => {
      titles.push(tocTitle);
      const n = titles.length;
      return `<h2 class="cr-section-title" id="sec-${n}"><span class="cr-sec-num">${n}.</span>${text}</h2>`;
    }
  );
  return { numbered, titles };
};

const renderToc = (titles) => {
  if (!titles.length) return '';
  return `
<nav class="cr-toc">
  <div class="cr-toc-title">Contents</div>
  <ol class="cr-toc-list">${titles.map((t, i) => `<li><a href="#sec-${i + 1}">${t}</a></li>`).join('')}</ol>
</nav>`;
};

const reportPage = (bodyParts, modelTitle, { toc = false, mastheadParts = [] } = {}) => {
  const raw = bodyParts.filter(Boolean).join('\n');
  const { numbered, titles } = numberSections(raw);
  const head = [
    ...mastheadParts.filter(Boolean),
    toc ? renderToc(titles) : '',
  ].filter(Boolean).join('\n');
  const body = head ? `${head}\n${numbered}` : numbered;
  const title = `Process Assessment Report${modelTitle ? ' \u2014 ' + toReportTitleCase(modelTitle) : ''}`;
  return `<!DOCTYPE html>\n<html lang="en">\n<head>\n<meta charset="utf-8"/>\n<meta name="renderer-version" content="v2025-03-17"/>\n<meta name="viewport" content="width=device-width, initial-scale=1"/>\n<link rel="preconnect" href="https://fonts.googleapis.com"/>\n<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin="anonymous"/>\n<link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet"/>\n<title>${esc(title)}</title>\n<style>${buildCSS()}</style>\n</head>\n<body>\n<div class="cr-wrap">\n${body}\n</div>\n</body>\n</html>`;
};

const noDataPage = (data) => `<!DOCTYPE html><html><body style="font-family:sans-serif;padding:24px;color:${P.ink}">No Assessment Report data available.<script id="__qa_data__" type="application/json">${JSON.stringify(data)}</script></body></html>`;

export const renderConsultingReportHTML = (data, modelTitle = '', opts = {}) => {
  applyReportTheme(opts.theme || DEFAULT_REPORT_THEME);
  if (!data || typeof data !== 'object') return noDataPage(data);
  return reportPage([
    ...buildReportBody(data),
    renderDocFooter(data.compiled_evidence_pack),
  ], modelTitle);
};

export const buildConsultingReportExportHTML = (data, modelTitle = '', opts = {}) => {
  applyReportTheme(opts.theme || DEFAULT_REPORT_THEME);
  if (!data || typeof data !== 'object') return noDataPage(data);
  return reportPage(
    [
      ...buildReportBody(data),
      renderDocFooter(data.compiled_evidence_pack),
    ],
    modelTitle,
    {
      toc: true,
      mastheadParts: [renderDocHeader(modelTitle, 'Process Assessment Report', data.compiled_evidence_pack)],
    }
  );
};

export const renderConsultingReportHtml = renderConsultingReportHTML;
export const renderConsultingReport = renderConsultingReportHTML;